import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import { getRouteConfig } from '@/app/router/routeConfig';

// JSON-LD Schema Types
type SchemaType =
  | 'WebPage'
  | 'WebApplication'
  | 'FAQPage'
  | 'ContactPage'
  | 'AboutPage'
  | 'ItemPage';

// Base URL for canonical URLs and assets
const BASE_URL =
  import.meta.env['VITE_APP_URL'] || 'https://app.domainboost.com';

// Organization schema (shared across pages)
const organizationSchema = {
  '@type': 'Organization',
  name: 'DomainBoost',
  url: BASE_URL,
  logo: `${BASE_URL}/logo.svg`,
  description:
    'Professional SEO services platform for advertisers - link building and content marketing',
  sameAs: [
    'https://twitter.com/domainboost',
    'https://linkedin.com/company/domainboost',
  ],
};

// WebApplication schema (for the platform)
const webApplicationSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebApplication',
  name: 'DomainBoost',
  description:
    'Professional SEO services platform for advertisers - link building and content marketing',
  url: BASE_URL,
  applicationCategory: 'BusinessApplication',
  operatingSystem: 'Web',
  offers: {
    '@type': 'Offer',
    price: '0',
    priceCurrency: 'USD',
  },
  publisher: organizationSchema,
};

// Schema type mapping for routes
const ROUTE_SCHEMA_TYPES: Record<string, SchemaType> = {
  '/dashboard': 'WebApplication',
  '/services': 'ItemPage',
  '/browse-sites': 'ItemPage',
  '/packages': 'ItemPage',
  '/orders': 'ItemPage',
  '/content-orders': 'ItemPage',
  '/wallet': 'WebPage',
  '/profile': 'WebPage',
  '/help': 'FAQPage',
  '/login': 'WebPage',
  '/register': 'WebPage',
};

// Supported languages for hreflang
const SUPPORTED_LANGUAGES = [
  { code: 'en', url: BASE_URL },
  { code: 'es', url: `${BASE_URL}/es` },
  { code: 'fr', url: `${BASE_URL}/fr` },
  { code: 'de', url: `${BASE_URL}/de` },
  { code: 'pt', url: `${BASE_URL}/pt` },
];

// OG Image mapping for different page types
const OG_IMAGES: Record<string, string> = {
  '/dashboard': '/og-images/dashboard.png',
  '/services': '/og-images/services.png',
  '/browse-sites': '/og-images/browse-sites.png',
  '/packages': '/og-images/packages.png',
  '/orders': '/og-images/orders.png',
  '/content-orders': '/og-images/content-orders.png',
  '/wallet': '/og-images/wallet.png',
  '/profile': '/og-images/profile.png',
  '/help': '/og-images/help.png',
  '/login': '/og-images/auth.png',
  '/register': '/og-images/auth.png',
  default: '/og-images/default.png',
};

/**
 * DocumentHead - Automatically sets document title and meta description based on route
 * Includes canonical URLs, Open Graph images, Twitter Card meta tags, and JSON-LD structured data
 */
export const DocumentHead: React.FC = () => {
  const location = useLocation();
  const routeConfig = getRouteConfig(location.pathname);

  // Default values
  const title = routeConfig?.title ?? 'DomainBoost';
  const description =
    routeConfig?.description ?? 'Professional domain boost and SEO services';
  const canonicalUrl = `${BASE_URL}${location.pathname}`;

  // Get OG image based on route
  const ogImagePath = OG_IMAGES[location.pathname] || OG_IMAGES.default;
  const ogImageUrl = `${BASE_URL}${ogImagePath}`;

  // Get schema type for current route
  const schemaType = ROUTE_SCHEMA_TYPES[location.pathname] || 'WebPage';

  // Generate WebPage JSON-LD schema
  const generatePageSchema = () => {
    return {
      '@context': 'https://schema.org',
      '@type': schemaType === 'WebApplication' ? 'WebPage' : schemaType,
      name: title,
      description: description,
      url: canonicalUrl,
      isPartOf: {
        '@type': 'WebSite',
        name: 'DomainBoost',
        url: BASE_URL,
      },
      publisher: organizationSchema,
    };
  };

  // Generate breadcrumb schema based on path
  const generateBreadcrumbSchema = () => {
    const pathSegments = location.pathname.split('/').filter(Boolean);
    if (pathSegments.length === 0) return null;

    const breadcrumbs = [
      { name: 'Home', url: '/' },
      ...pathSegments.map((segment, index) => ({
        name:
          segment.charAt(0).toUpperCase() + segment.slice(1).replace(/-/g, ' '),
        url: `/${pathSegments.slice(0, index + 1).join('/')}`,
      })),
    ];

    return {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: breadcrumbs.map((item, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        name: item.name,
        item: `${BASE_URL}${item.url}`,
      })),
    };
  };

  const pageSchema = generatePageSchema();
  const breadcrumbSchema = generateBreadcrumbSchema();

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{title}</title>
      <meta name="description" content={description} />

      {/* Canonical URL */}
      <link rel="canonical" href={canonicalUrl} />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content="website" />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:site_name" content="DomainBoost" />
      <meta property="og:image" content={ogImageUrl} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={title} />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={canonicalUrl} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImageUrl} />
      <meta name="twitter:image:alt" content={title} />

      {/* hreflang for internationalization */}
      {SUPPORTED_LANGUAGES.map((lang) => (
        <link
          key={lang.code}
          rel="alternate"
          hrefLang={lang.code}
          href={`${lang.url}${location.pathname}`}
        />
      ))}
      <link rel="alternate" hrefLang="x-default" href={canonicalUrl} />

      {/* JSON-LD Structured Data - WebApplication (on dashboard) */}
      {location.pathname === '/dashboard' && (
        <script type="application/ld+json">
          {JSON.stringify(webApplicationSchema)}
        </script>
      )}

      {/* JSON-LD Structured Data - Page Schema */}
      <script type="application/ld+json">{JSON.stringify(pageSchema)}</script>

      {/* JSON-LD Structured Data - Breadcrumbs */}
      {breadcrumbSchema && (
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
      )}
    </Helmet>
  );
};
